import { useIsInternalUser } from 'hooks/useIsInternalUser';
import { useToggle } from 'hooks/useToggle';
import { useWorkflowErrorsUtils } from 'hooks/useWorkflowErrorsUtils';
import type { IntlKey } from 'locales';
import type { NextWorkflowError } from 'models/NextWorkflowError';
import type { QuoteWarning } from 'models/QuoteWarning';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Overlay, Popover, PopoverBody } from 'react-bootstrap';
import styled from 'styled-components';

import { WorkflowErrorDetailsModal } from '../WorkflowError/WorkflowErrorDetailsModal';
import { ErrorTooltipBody } from './ErrorTooltipBody/ErrorTooltipBody';
import { ErrorTooltipFooter } from './ErrorTooltipFooter/ErrorTooltipFooter';
import { ErrorTooltipHeader } from './ErrorTooltipHeader/ErrorTooltipHeader';
import type { WithErrorTooltipColor, WithErrorTooltipPosition } from './model';

type WithErrorTooltipSeverity = 'error' | 'warning';

export interface WithErrorTooltipProps {
  canCopy?: boolean;
  className?: string;
  contentToCopy?: string;
  e2e?: string;
  errors?: readonly NextWorkflowError[];
  onDismiss?(): void;
  overrideErrorMessages?: readonly string[];
  position?: WithErrorTooltipPosition;
  productSubtype?: string;
  errorTitle?: IntlKey;
  warningTitle?: IntlKey;
  warnings?: readonly QuoteWarning[];
  children?: ReactNode;
  hideTooltipOnStartup?: boolean;
  dismissOnMouseLeave?: boolean;
}

export const PopoverContentContainer = styled.div`
  width: 13rem;
`;

export function WithErrorTooltip({
  canCopy,
  children,
  contentToCopy,
  e2e,
  errors = [],
  hideTooltipOnStartup = false,
  onDismiss,
  overrideErrorMessages = [],
  position = 'bottom',
  productSubtype,
  errorTitle = 'errorDetails.standardMessage',
  warningTitle = 'warning',
  warnings = [],
  dismissOnMouseLeave = false,
  className,
}: WithErrorTooltipProps) {
  const ref = useRef<HTMLDivElement>(null);

  const isInternal = useIsInternalUser();
  const { getWorkflowErrorsSummary } = useWorkflowErrorsUtils();

  const [isTooltipOn, setTooltipOn] = useState(false);

  const { isOn: isDetailsOn, toggle: toggleDetails, toggleOn: toggleDetailsOn } = useToggle(false);

  const shouldShowDetails = useMemo(() => isInternal && errors.length > 0, [isInternal, errors]);

  const severity = useMemo<WithErrorTooltipSeverity>(() => {
    if (errors.length > 0 || overrideErrorMessages.length > 0) {
      return 'error';
    } else if (warnings.length > 0) {
      return 'warning';
    } else {
      return 'error';
    }
  }, [errors, warnings, overrideErrorMessages]);

  const color = useMemo(() => getTooltipColor(severity), [severity]);
  const title = useMemo(
    () => (severity === 'warning' ? warningTitle : errorTitle),
    [severity, errorTitle, warningTitle],
  );
  const messages = useMemo(() => {
    if (errors.length > 0) {
      return getWorkflowErrorsSummary(errors, !isInternal, productSubtype);
    } else if (overrideErrorMessages.length > 0) {
      return overrideErrorMessages;
    } else if (warnings.length > 0) {
      return warnings.map(w => w.message);
    } else {
      return ['generic.error.message'];
    }
  }, [errors, warnings, overrideErrorMessages, isInternal, productSubtype, getWorkflowErrorsSummary]);

  useEffect(() => {
    if (!hideTooltipOnStartup) {
      setTooltipOn(true);
    }
  }, [hideTooltipOnStartup]);

  const onViewDetails = useCallback(() => {
    toggleDetailsOn();
    setTooltipOn(false);
  }, [toggleDetailsOn]);

  const onDismissed = useCallback(() => {
    if (onDismiss) {
      onDismiss();
    }
    setTooltipOn(false);
  }, [onDismiss]);

  const onHover = useCallback(() => {
    setTooltipOn(true);
  }, []);
  const handleMouseLeave = useCallback(() => {
    if (dismissOnMouseLeave) {
      setTooltipOn(false);
    }
  }, [dismissOnMouseLeave]);

  return (
    <>
      <div
        onMouseEnter={onHover}
        onMouseLeave={handleMouseLeave}
        ref={ref}
        role="tooltip-container"
        className={className}
      >
        {children}
      </div>
      <Overlay target={ref} show={isTooltipOn} placement={position} transition={true}>
        <Popover
          id={`${e2e}-popover`}
          data-e2e="tooltip-container"
          role="tooltip"
          color={color}
          placement={position}
          className={`popover-arrow-${color} popover-arrow-fill`}
        >
          <PopoverContentContainer data-e2e="with-error-tooltip">
            <PopoverBody className={`border border-${color} p-0`}>
              <div className={`bg-${color} p-1`}>
                <ErrorTooltipHeader canCopy={canCopy && isInternal} content={contentToCopy} title={title} />
              </div>
              <div className="p-0">
                <ErrorTooltipBody messages={messages} />
                <ErrorTooltipFooter
                  onDismiss={onDismissed}
                  onViewDetails={onViewDetails}
                  shouldShowDetails={shouldShowDetails}
                />
              </div>
            </PopoverBody>
          </PopoverContentContainer>
        </Popover>
      </Overlay>
      <WorkflowErrorDetailsModal
        isOpen={isDetailsOn}
        toggle={toggleDetails}
        errors={errors}
        shouldDisplayErrorDetails={shouldShowDetails}
      />
    </>
  );
}

function getTooltipColor(severity: WithErrorTooltipSeverity): WithErrorTooltipColor {
  switch (severity) {
    case 'warning':
      return 'warning';
    case 'error':
      return 'danger';
  }
}
